// src/components/OrderForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebaseConfig';
import { collection, serverTimestamp, doc, runTransaction, query, getDocs, Timestamp } from 'firebase/firestore';
// File upload utilities are imported dynamically when needed
import { useOrganization } from '../contexts/OrganizationContext';
import { useError } from '../contexts/ErrorContext';
import { useSessionTimeout } from './order-management/hooks/useSessionTimeout';
import OrderNavigation from './OrderNavigation';
import ImageUploader from './ImageUploader';
import { getPlatformClass, normalizePartyNameForStorage, normalizePartyNameForDisplay, getPlatformType } from '../utils/platformUtils';
import { validateName, validateText, validateFiles, sanitizeInput } from '../utils/validationUtils';
import PlatformLayout from './PlatformLayout';
import SubscriptionService from '../services/SubscriptionService';
import { useTranslation } from '../hooks/useTranslation';
import { createFileBackupInfo } from '../utils/fileRetentionUtils';

import Fuse from 'fuse.js';
import clsx from 'clsx';
import styles from './OrderForm.module.css';

function OrderForm() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const { organizationId, userRole } = useOrganization();
  const { showErrorToast } = useError();
  const { t } = useTranslation('orders');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderData, setOrderData] = useState({
    partyName: '',
    orderDetails: '',
    orderDetailsImages: [],
    additionalNotes: '',
    status: 'PLACED',
    autoDelete: false,
    assignedTo: ''
  });
  const [partyOptions, setPartyOptions] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [inputType, setInputType] = useState('text'); // 'text' or 'image'
  const [isOrderSettingsOpen, setIsOrderSettingsOpen] = useState(false);
  const [availableSalespeople, setAvailableSalespeople] = useState([]);
  const platformType = getPlatformType();
  const isMobileAppBar = ['ios', 'android', 'ios-pwa', 'android-pwa'].includes(platformType);

  // Use session timeout hook
  useSessionTimeout(user, navigate);

  // Get available tabs based on user role
  const getAvailableTabs = () => {
    if (userRole === 'InventoryManager') {
      return ['APPROVED', 'READY_TO_DISPATCH', 'DISPATCHED'];
    }
    return ['PLACED', 'APPROVED', 'READY_TO_DISPATCH', 'DISPATCHED'];
  };

  const availableTabs = getAvailableTabs();

  // Fetch available salespeople for assignment
  useEffect(() => {
    async function fetchSalespeople() {
      if (!organizationId || !user || (userRole !== 'BusinessOwner' && userRole !== 'Accountant')) return;
      
      try {
        const usersRef = collection(db, `organizations/${organizationId}/users`);
        const q = query(usersRef);
        const snapshot = await getDocs(q);
        const salespeople = [];
        
        snapshot.forEach(doc => {
          const userData = doc.data();
          if (userData.role === 'Salesperson' && userData.approved) {
            salespeople.push({
              id: doc.id,
              fullName: userData.fullName,
              email: userData.email
            });
          }
        });
        
        setAvailableSalespeople(salespeople);
      } catch (error) {
        // Error fetching salespeople - handled silently
      }
    }
    
    fetchSalespeople();
  }, [organizationId, user, userRole]);

  useEffect(() => {
    // Fetch customer names based on role
    async function fetchPartyNames() {
      if (!organizationId || !user) return;
      const ordersRef = collection(db, `organizations/${organizationId}/orders`);
      const q = query(ordersRef);
      const snapshot = await getDocs(q);
      const parties = new Set();
      snapshot.forEach(doc => {
        const data = doc.data();
        if (
          (userRole === 'Salesperson' && (data.createdBy === user.uid || data.assignedTo === user.uid) && data.partyName) ||
          ((userRole === 'BusinessOwner' || userRole === 'Accountant') && data.partyName)
        ) {
          // Normalize customer name for display (capitalize first letter of each word)
          parties.add(normalizePartyNameForDisplay(data.partyName));
        }
      });
      setPartyOptions(Array.from(parties));
    }
    fetchPartyNames();
  }, [organizationId, user, userRole]);

  // Fuzzy search with Fuse.js
  useEffect(() => {
    if (!orderData.partyName) {
      setFilteredOptions(partyOptions);
      return;
    }
    const fuse = new Fuse(partyOptions, { threshold: 0.3 });
    const results = fuse.search(orderData.partyName).map(r => r.item);
    setFilteredOptions(results);
  }, [orderData.partyName, partyOptions]);

  const handleChange = (e) => {
    setOrderData({ ...orderData, [e.target.name]: e.target.value });
  };

  const handlePartyInputChange = (e) => {
    setOrderData({ ...orderData, partyName: e.target.value });
    setShowOptions(true);
  };

  const handleOptionClick = (option) => {
    setOrderData({ ...orderData, partyName: option });
    setShowOptions(false);
  };

  const handleImageUpload = (files) => {
    
    // Use new validation utility
    const filesValidation = validateFiles(files, {
      allowedTypes: ['image/*'],
      maxFiles: 10
    });
    
    if (!filesValidation.isValid) {
      showErrorToast(filesValidation.error, {
        variant: 'warning',
        autoHide: true,
        duration: 4000
      });
      return;
    }
    
    setOrderData({ ...orderData, orderDetailsImages: [...orderData.orderDetailsImages, ...files] });
  };

  const handleRemoveImage = (index) => {
    
    setOrderData({
      ...orderData,
      orderDetailsImages: orderData.orderDetailsImages.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return;
    }
    if (!user) {
      showErrorToast(t('errors.pleaseLoginFirst'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }
    if (!organizationId) {
      showErrorToast(t('errors.organizationContextNotFound'), {
        variant: 'error',
        autoHide: true,
        duration: 4000
      });
      return;
    }
    
    // Check order limit before validation
    try {
      const orderLimit = await SubscriptionService.checkOrderLimit(organizationId);
      if (!orderLimit.allowed) {
        showErrorToast(t('errors.orderLimitReached'), {
          variant: 'warning',
          autoHide: true,
          duration: 6000
        });
        return;
      }
    } catch (error) {
      showErrorToast(t('errors.errorCheckingOrderLimit'), {
        variant: 'error',
        autoHide: true,
        duration: 5000
      });
      return;
    }
    
    // Use new validation utilities
    const partyNameValidation = validateName(orderData.partyName, 'Customer Name', {
      allowAlphanumeric: true // Allow numbers and common business characters for customer names
    });
    if (!partyNameValidation.isValid) {
      showErrorToast(partyNameValidation.error, {
        variant: 'warning',
        autoHide: true,
        duration: 4000
      });
      return;
    }
    
    if (inputType === 'text') {
      const orderDetailsValidation = validateText(orderData.orderDetails, 'Order Details', {
        required: true,
        minLength: 1,
        maxLength: 1000
      });
      if (!orderDetailsValidation.isValid) {
        showErrorToast(orderDetailsValidation.error, {
          variant: 'warning',
          autoHide: true,
          duration: 4000
        });
        return;
      }
    }
    
    if (inputType === 'image' && orderData.orderDetailsImages.length === 0) {
      showErrorToast(t('validation.imageRequired'), {
        variant: 'warning',
        autoHide: true,
        duration: 4000
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Sanitize inputs before processing
      const sanitizedPartyName = sanitizeInput(orderData.partyName);
      const sanitizedOrderDetails = inputType === 'text' ? sanitizeInput(orderData.orderDetails) : '';
      const sanitizedAdditionalNotes = sanitizeInput(orderData.additionalNotes || '');
      
      // Upload all images if present using retry logic and offline detection
      let orderDetailsImageURLs = [];
      if (orderData.orderDetailsImages.length > 0) {
        const { uploadFilesWithRetry } = await import('../utils/fileUploadUtils');
        
        // Show progress toast
        showErrorToast(t('errors.uploadingFiles', { count: orderData.orderDetailsImages.length }), {
          variant: 'info',
          autoHide: true,
          duration: 3000
        });
        
        const uploadResults = await uploadFilesWithRetry(
          orderData.orderDetailsImages,
          `${organizationId}/orderDetails`,
          {
            maxRetries: 3,
            onOverallProgress: (completed, total) => {
              if (completed < total) {
                // Progress updates could be shown here if needed
              }
            },
            onFileProgress: (fileIndex, success, downloadURL, error) => {
              if (error) {
                // File upload progress - handled silently
              }
            }
          }
        );
        
        // Extract successful uploads
        orderDetailsImageURLs = uploadResults.successful.map(result => result.downloadURL);
        
        // Handle failed uploads
        if (uploadResults.failed.length > 0) {
          const failedCount = uploadResults.failed.length;
          const successCount = uploadResults.successful.length;
          
          if (successCount === 0) {
            // All files failed
            throw new Error(t('errors.failedUploadFiles', { count: failedCount }));
          } else {
            // Some files failed, warn user but continue
            showErrorToast(
              t('errors.warningFilesFailed', { failedCount, successCount }),
              {
                variant: 'warning',
                autoHide: true,
                duration: 6000
              }
            );
          }
        }
      }

      // Calculate deletion date if auto-delete is enabled
      // FIX: Convert JavaScript Date to Firestore Timestamp for proper 10-day deletion
      const deletionDate = orderData.autoDelete ? 
        Timestamp.fromDate(new Date(Date.now() + 10 * 24 * 60 * 60 * 1000)) : // 10 days from now
        null;
      
      // Create order document reference before transaction
      const ordersRef = collection(db, `organizations/${organizationId}/orders`);
      const newOrderRef = doc(ordersRef);
      
      await runTransaction(db, async (transaction) => {
        const orgRef = doc(db, `organizations/${organizationId}`);
        const orgDoc = await transaction.get(orgRef);
        const orgData = orgDoc.data();
        const currentOrderNumber = orgData?.lastOrderNumber || 0;
        const nextOrderNumber = currentOrderNumber + 1;
        const planId = orgData?.subscriptionPlan || 'basic';
        
        // Update organization's lastOrderNumber
        transaction.update(orgRef, { lastOrderNumber: nextOrderNumber });
        
        // Create file backup info object (atomic with order creation)
        const fileBackupInfo = createFileBackupInfo(planId, Timestamp.now(), {});
        
        const orderDataToSave = {
          partyName: normalizePartyNameForStorage(sanitizedPartyName),
          orderDetails: inputType === 'text' ? sanitizedOrderDetails : '',
          orderDetailsImageURLs: orderDetailsImageURLs,
          additionalNotes: sanitizedAdditionalNotes,
          orderNumber: nextOrderNumber,
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          status: 'PLACED',
          autoDelete: orderData.autoDelete,
          deletionDate: deletionDate, // FIX: Use Firestore Timestamp directly, not serverTimestamp()
          assignedTo: orderData.assignedTo,
          fileBackupInfo: fileBackupInfo // FIX: Initialize file backup info atomically
        };
        
        // FIX: Use transaction.set() instead of addDoc() for atomic transaction
        transaction.set(newOrderRef, orderDataToSave);
      });
      
      showErrorToast(t('errors.orderCreatedSuccessfully'), {
        variant: 'info',
        autoHide: true,
        duration: 3000
      });
      navigate('/orders');
    } catch (error) {
      showErrorToast(t('errors.orderError', { message: error.message }), {
        variant: 'error',
        autoHide: true,
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PlatformLayout>
      <div className={`container ${getPlatformClass()}`}>
        <OrderNavigation
          currentTab={null}
          setCurrentTab={() => {}}
          availableTabs={availableTabs}
          userRole={userRole}
          isActiveScreen="new-order"
        />

        <div className="profile-header-wrapper">
          {isMobileAppBar ? (
            <div className="mobile-app-bar">
              <h1 className="profile-title">{t('form.title')}</h1>
            </div>
          ) : (
            <div className="saas-header">
              <div className="saas-header-content">
                <h1 className="saas-title">{t('form.title')}</h1>
                <p className="saas-subtitle">{t('form.subtitle')}</p>
              </div>
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className={`${styles.formWrapper} form-complex`}>
          <div className={`${styles.field} ${styles.fieldRelative}`}>
            <label htmlFor="partyName" className={styles.label}>Customer Name</label>
            <input
              type="text"
              id="partyName"
              name="partyName"
              value={orderData.partyName}
              onChange={handlePartyInputChange}
              placeholder={t('form.placeholders.enterCustomerName')}
              autoComplete="off"
              required
              onFocus={() => setShowOptions(true)}
              className={`${styles.input} ${styles.inputRelative}`}
              onBlur={() => setTimeout(() => setShowOptions(false), 150)}
            />
            {showOptions && (filteredOptions.length > 0 || orderData.partyName.trim()) && (
              <ul className={styles.autocompleteOptions}>
                {filteredOptions.map(option => (
                  <li
                    key={option}
                    className={styles.autocompleteOption}
                    onMouseDown={() => handleOptionClick(option)}
                  >
                    {option}
                  </li>
                ))}
                {!filteredOptions.includes(orderData.partyName) && orderData.partyName.trim() && (
                  <li className={styles.autocompleteOptionNew}>
                    Add new: <strong>{orderData.partyName}</strong>
                  </li>
                )}
              </ul>
            )}
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Order Details</label>
            <div className={styles.orderDetailsToggle}>
              <button
                type="button"
                className={clsx(styles.toggleButton, inputType === 'text' && styles.active)}
                onClick={() => setInputType('text')}
              >
                Text
              </button>
              <button
                type="button"
                className={clsx(styles.toggleButton, inputType === 'image' && styles.active)}
                onClick={() => setInputType('image')}
              >
                Image
              </button>
            </div>
            
            {inputType === 'text' ? (
              <textarea
                id="orderDetails"
                name="orderDetails"
                value={orderData.orderDetails}
                onChange={handleChange}
                placeholder={t('form.placeholders.enterOrderDetails')}
                required
                rows={5}
                className={styles.textarea}
              />
            ) : (
              <>
                <ImageUploader 
                  onImagesSelected={handleImageUpload}
                  multiple={true}
                  currentImages={orderData.orderDetailsImages}
                  onRemoveImage={handleRemoveImage}
                />
                {orderData.orderDetailsImages.length > 0 && (
                  <ul className={styles.fileList}>
                    {orderData.orderDetailsImages.map((file, idx) => (
                      <li key={idx} className={styles.fileItem}>
                        <span className={styles.fileName}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(idx)}
                          className={styles.removeButton}
                          aria-label={t('form.ariaLabels.removeFile', { fileName: file.name })}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>

          <div className={styles.field}>
            <label htmlFor="additionalNotes" className={styles.label}>Additional Notes</label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              value={orderData.additionalNotes}
              onChange={handleChange}
              placeholder={t('form.placeholders.enterAdditionalNotes')}
              rows={3}
              className={styles.textarea}
            />
          </div>

          {/* Order Settings Dropdown */}
          <div className={styles.orderSettingsDropdown}>
            <div
              onClick={() => setIsOrderSettingsOpen(!isOrderSettingsOpen)}
              className={styles.orderSettingsHeader}
            >
              <h3 className={styles.orderSettingsTitle}>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Order Settings
              </h3>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={clsx(styles.orderSettingsIcon, isOrderSettingsOpen && styles.orderSettingsIconRotated)}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
            {isOrderSettingsOpen && (
              <div className={styles.orderSettingsContent}>
                {/* Salesperson Assignment - Only for BusinessOwners and Accountants */}
                {(userRole === 'BusinessOwner' || userRole === 'Accountant') && (
                  <div className={`${styles.field} ${styles.orderSettingsField}`}>
                    <label htmlFor="assignedTo" className={styles.label}>
                      Assign to Salesperson
                      <span className={styles.orderSettingsOptional}>
                        (Optional)
                      </span>
                    </label>
                    <select
                      id="assignedTo"
                      name="assignedTo"
                      value={orderData.assignedTo}
                      onChange={handleChange}
                      className={`${styles.select} platform-select`}
                    >
                      <option value="">Select a salesperson</option>
                      {availableSalespeople.map(salesperson => (
                        <option key={salesperson.id} value={salesperson.id}>
                          {salesperson.fullName}
                        </option>
                      ))}
                    </select>
                    {orderData.assignedTo && (
                      <div className="assignment-notice">
                        <strong>Note:</strong> This order will be assigned to the selected salesperson and they will have full access to manage it.
                      </div>
                    )}
                  </div>
                )}
                
                <div className={styles.autoDeleteRow}>
                  <span className={styles.autoDeleteText}>
                    Auto-delete order
                    <span className={styles.tooltipContainer} tabIndex={0}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        className={styles.tooltipIcon}
                      >
                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                        <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2" />
                        <circle cx="12" cy="16" r="1" />
                      </svg>
                      <span className="tooltip-text">This will auto delete your order in 10 days</span>
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    className="auto-delete-checkbox"
                    name="autoDelete"
                    checked={orderData.autoDelete}
                    onChange={(e) => setOrderData({ ...orderData, autoDelete: e.target.checked })}
                  />
                </div>
              </div>
            )}
          </div>

          <div className={styles.actions}>
            <button 
              type="submit" 
              className={styles.submit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating Order...' : 'Create Order'}
            </button>
            <button 
              type="button" 
              onClick={() => navigate('/orders')} 
              className={styles.cancel}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </PlatformLayout>
  );
}

export default OrderForm;


